// src/modulos/Parametros/RegionPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const REGION_COLLECTION = 'parameters/data/regioes';
const ITEMS_PER_PAGE = 10;

const RegionPage = () => {
    const can = usePagePermissions('regiao');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: regions,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(REGION_COLLECTION, 'name');

    // ============================================================
    // 1. DEFINIR filteredRegions ANTES dos callbacks que o usam
    // ============================================================
    const filteredRegions = useMemo(() => {
        if (!regions) return [];
        if (!searchTerm) return regions;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return regions.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearch)
        );
    }, [regions, searchTerm]);

    const totalFilteredItems = filteredRegions.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentRegions = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredRegions.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredRegions, currentPage]);

    // Verificar se uma região está sendo usada
    const checkRegionInUse = useCallback(async (regionId) => {
        try {
            const { collection, query, where, getDocs } = await import('firebase/firestore');
            const { db } = await import('../../firebaseConfig');

            const collectionsToCheck = ['customers', 'sellers', 'priceTables'];
            const usedIn = [];

            for (const collectionName of collectionsToCheck) {
                try {
                    const q = query(collection(db, collectionName), where('regionId', '==', regionId));
                    const snapshot = await getDocs(q);
                    if (!snapshot.empty) {
                        usedIn.push(collectionName);
                    }
                } catch (err) {
                    console.warn(`Could not check ${collectionName}:`, err);
                }
            }

            return {
                isInUse: usedIn.length > 0,
                usedIn: usedIn
            };
        } catch (error) {
            console.error('Error checking region usage:', error);
            return { isInUse: false, usedIn: [] };
        }
    }, []);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setIsModalOpen(false);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setIsModalOpen(true);
        setNameInput(item.name);
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const inUseCheck = await checkRegionInUse(id);

            if (inUseCheck.isInUse) {
                const collectionsMap = {
                    'customers': 'clientes',
                    'sellers': 'vendedores',
                    'priceTables': 'tabelas de preço'
                };

                const usedInNames = inUseCheck.usedIn.map(col => collectionsMap[col] || col).join(', ');
                setFormError(`Não é possível excluir esta região pois ela está sendo utilizada em: ${usedInNames}. Remova as referências antes de excluir.`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setDeleteLoading(false);
                setTimeout(() => setFormError(''), 5000);
                return;
            }

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Região "${name}" deletada com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const remainingItems = filteredRegions.length - 1;
                if (currentPage > 1 && remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Região: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            console.error('Delete error:', error);
            setFormError("Erro ao verificar uso da região: " + error.message);
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, checkRegionInUse, deleteDocument, filteredRegions, currentPage]);

    // Verificar duplicados
    const isDuplicateName = useCallback((name, excludeId = null) => {
        const normalizedName = name.trim().toLowerCase();
        return regions?.some(region =>
            region.name.toLowerCase() === normalizedName &&
            region.id !== excludeId
        );
    }, [regions]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        const trimmedName = nameInput.trim();

        if (!trimmedName) {
            setFormError("O campo Região é obrigatório.");
            return;
        }

        if (isDuplicateName(trimmedName, editingItem?.id)) {
            setFormError("Já existe uma região com este nome. Por favor, utilize um nome diferente.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: trimmedName,
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Região editada com sucesso!' : 'Região adicionada com sucesso!');
                setTimeout(() => {
                    resetForm();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError(`Erro inesperado: ${error.message}`);
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, editingItem, isDuplicateName, addDocument, updateDocument, resetForm]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Nova Região'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Região'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Região <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        placeholder="Nome da Região (ex: Sudeste, Regional 1)"
                        required
                        className={styles.input}
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Digite o nome da região. Este nome será usado em clientes, vendedores e tabelas de preço.
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar a região "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita. Regiões vinculadas a clientes, vendedores ou tabelas de preço não podem ser excluídas.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Regiões"
                actionText="Adicionar Região"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome da região..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : currentRegions.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '🗺️'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhuma região cadastrada'
                            }
                            message={
                                searchTerm
                                    ? `Nenhuma região corresponde a "${searchTerm}". Tente outro termo.`
                                    : "Clique em 'Adicionar Região' para começar."
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Região</th>
                                            <th style={{ width: '180px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRegions.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Atualizado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="regiões"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default RegionPage;